<script setup lang="ts">
  import { ref, onMounted, computed, onUnmounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { NModal, NButton, NSpin, NInput } from 'naive-ui'
  import { isEmpty, showMessage } from '@/common/utils'
  import { emitter } from '@/common/mitt'
  import { useWindowSize } from '@vueuse/core'
  import imgLogoPhotoup from '@/assets/logo/photoup-2.webp'
  import imgLogoRemovebg from '@/assets/logo/removebg-2.webp'
  import { apiFetchEmailCode } from '@/api/user'
  import { useUserStore } from '@/store/modules/useUserStore'

  const { VITE_APP_NAME } = import.meta.env,
    userStore = useUserStore(),
    router = useRouter(),
    route = useRoute()

  let showModalRef = ref(false),
    // 1 google; 2 apple; 3 fetch code; 4 email
    loadingTypeRef = ref(0),
    inputEmailRef = ref(''),
    inputCodeRef = ref(''),
    clientCode = '',
    intervalId = null,
    secondsRef = ref(0)

  let imgLogo = computed(() => {
    let logo
    switch (VITE_APP_NAME) {
      case 'removebg.pro':
        logo = imgLogoRemovebg
        break
      default:
        logo = imgLogoPhotoup
        break
    }
    return logo
  })

  onMounted(async () => {
    emitter.on('loginSuccess', () => {
      hide()
    })
  })
  onUnmounted(() => {
    emitter.off('loginSuccess')
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
  })
  const show = () => {
    setTimeout(() => {
      loadingTypeRef.value = 0
      inputEmailRef.value = ''
      inputCodeRef.value = ''
      showModalRef.value = true
    }, 200)
  }
  const hide = () => {
    showModalRef.value = false
  }
  const isLoading = () => {
    let type = loadingTypeRef.value
    if (type !== 0) {
      showMessage(
        `${type === 3 ? 'Fetching' : 'Logging'} progress, please wait...`,
        'warning'
      )
      return true
    }
    return false
  }
  const onContinueWithGoogle = () => {
    if (isLoading()) {
      return
    }
    loadingTypeRef.value = 1
    navToAuth0ForLogin('google-oauth2')
  }
  const onContinueWithApple = () => {
    if (isLoading()) {
      return
    }
    loadingTypeRef.value = 2
    navToAuth0ForLogin('apple')
  }
  const onFetchCode = () => {
    if (secondsRef.value > 0) {
      return
    }
    if (isLoading()) {
      return
    }
    if (!inputEmailRef.value) {
      showMessage('Please enter your email.', 'warning')
      return
    }
    loadingTypeRef.value = 3
    inputCodeRef.value = ''
    clientCode = ''
    apiFetchEmailCode({
      email: inputEmailRef.value,
      team: VITE_APP_NAME,
    })
      .then((data) => {
        showMessage(data.message, 'success')
        clientCode = data.client_code
        startCountdown()
      })
      .catch((err) => {
        showMessage(err, 'error')
      })
      .finally(() => {
        loadingTypeRef.value = 0
      })
  }
  const onContinueWithEmail = () => {
    if (isLoading()) {
      return
    }
    if (!inputEmailRef.value) {
      showMessage('Please enter your email.', 'warning')
      return
    }
    if (!inputCodeRef.value) {
      showMessage('Please enter your verification code.', 'warning')
      return
    }
    userStore
      .loginWithEmail({
        email: inputEmailRef.value,
        code: inputCodeRef.value,
        client_code: clientCode,
      })
      .then((data) => {
        showMessage('Login success', 'success')
      })
      .catch((err) => {
        showMessage(err, 'error')
      })
      .finally(() => {
        loadingTypeRef.value = 0
      })
  }
  const onClose = () => {
    if (isLoading()) {
      return
    }
    hide()
  }
  const onLink = (link) => {
    if (isLoading()) {
      return
    }
    hide()
    router.push(link)
  }
  const startCountdown = () => {
    clearInterval(intervalId)
    secondsRef.value = 60
    intervalId = setInterval(() => {
      secondsRef.value--
      if (secondsRef.value == 0) {
        clearInterval(intervalId)
        intervalId = null
      }
    }, 1000)
  }
  const navToAuth0ForLogin = (provider) => {
    let origon = window.location.origin
    if (!origon) {
      origon = window.location.protocol + '//' + window.location.host
    }
    const domain = 'dev-dsvtpkwfz0rls6yh.us.auth0.com',
      clientId = 'kvX8tkf35J5lKbbJHIEAL8lJmWQlvhJe',
      redirectUri = encodeURIComponent(origon + '/login/callback'),
      scope = 'openid email profile'

    const auth0Url =
      `https://${domain}/authorize` +
      `?response_type=code` +
      `&client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&scope=${scope}` +
      `&connection=${provider}` +
      `&state=${provider}`

    // 传到我们自己的login/callback页面的参数中只有 cope 和 state，所以不能通过 query 来传 target-path，暂用 localStorage 来保存
    localStorage.setItem('auth0-login-target-path', route.fullPath)
    window.location.href = auth0Url
    // openNewWindow(auth0Url)
  }
  const openNewWindow = (url) => {
    const screen_w = window.screen.availWidth,
      screen_h = window.screen.availHeight,
      window_x = window.screenX ?? window.screenLeft,
      window_y = window.screenY ?? window.screenTop,
      window_w = Math.ceil(screen_w * 0.48),
      window_h = Math.ceil(screen_h * 0.68),
      left = (screen_w - window_w) / 2 + window_x,
      top = (screen_h - window_h) / 2,
      features = `width=${window_w},height=${window_h},top=${top},left=${left},resizable=yes,scrollbars=yes,status=no,menubar=no,toolbar=no`
    const new_window = window.open(url, 'Login...', features)
    let open_result = 'success'
    if (
      !new_window ||
      new_window.closed ||
      typeof new_window.closed === 'undefined'
    ) {
      window.location.href = url
      open_result = 'fail'
    } else {
      new_window.focus()
    }
  }
  defineExpose({
    show,
    hide,
  })
</script>

<template>
  <n-modal v-model:show="showModalRef" :mask-closable="loadingTypeRef === 0">
    <div class="dialog-content">
      <img
        class="btn-close"
        src="@/assets/icon/ic-close.png"
        @click="onClose"
      />
      <img class="img-logo" :src="imgLogo" alt="" />
      <!-- Create an account or Login -->
      <span class="description">Sign up to get 10 free credits</span>
      <div class="btn-login" @click="onContinueWithGoogle">
        <n-spin v-if="loadingTypeRef === 1" stroke="#303133" />
        <img v-else src="@/assets/icon/ic-google.png" />
        <span>Continue with Google</span>
      </div>
      <div class="btn-login" @click="onContinueWithApple">
        <n-spin v-if="loadingTypeRef === 2" stroke="#303133" />
        <img v-else src="@/assets/icon/ic-apple.png" />
        <span>Continue with Apple</span>
      </div>
      <div class="line-or">
        <div />
        <span>or</span>
        <div />
      </div>
      <n-input
        :autofocus="false"
        type="text"
        placeholder="Email"
        autocomplete="email"
        v-model:value="inputEmailRef"
      />
      <div class="code-row">
        <n-input
          :autofocus="false"
          style="width: 68%; margin-right: 2%"
          type="text"
          placeholder="Verification Code"
          v-model:value="inputCodeRef"
        />
        <div
          class="btn-login"
          style="width: 32%"
          @click="onFetchCode"
          :style="{
            cursor: `${
              secondsRef > 0 || loadingTypeRef === 3 ? 'not-allowed' : 'pointer'
            }`,
          }"
        >
          <n-spin v-if="loadingTypeRef === 3" stroke="#303133" />
          <span>{{ secondsRef > 0 ? `${secondsRef}s` : 'Fetch' }}</span>
        </div>
      </div>
      <div class="btn-login" @click="onContinueWithEmail">
        <n-spin v-if="loadingTypeRef === 4" stroke="#303133" />
        <span>Continue with Email</span>
      </div>
      <div class="footer">
        <div>
          By continuing, you agree to our&nbsp;
          <div class="link" @click="onLink('/legal#terms')">Service Terms</div>
        </div>
        <div>
          and acknowledge our&nbsp;
          <div class="link" @click="onLink('/legal#privacy')">
            Privacy Policy
          </div>
        </div>
      </div>
    </div>
  </n-modal>
</template>

<style lang="scss" scoped>
  .dialog-content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: min(26rem, 96vw);
    height: 40rem;
    border-radius: 1.6rem;
    background: #fff;
  }

  .btn-close {
    position: absolute;
    width: 1.2rem;
    height: 1.2rem;
    top: 1.4rem;
    right: 1.4rem;
    opacity: 0.8;
    cursor: pointer;
  }

  .img-logo {
    height: 2rem;
    margin-top: 3.2rem;
  }

  .description {
    margin-top: 1.2rem;
    font-size: 1rem;
    color: var(--color-text-primary);
  }

  .btn-login {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 86%;
    height: 3.6rem;
    margin-top: 1.2rem;
    box-sizing: border-box;
    border: 1px #cdcdcd solid;
    border-radius: 0.8rem;
    transition: background-color 0.3s ease, transform 0.3s ease;
    cursor: pointer;

    img {
      width: 1.8rem;
      height: 1.8rem;
    }

    span {
      margin-left: 0.4rem;
      font-size: 1rem;
      color: var(--color-text-primary);
    }
  }

  .btn-login:hover {
    transform: scale(1.02);
    background-color: #ededed;
  }

  .line-or {
    width: 86%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-top: 1.2rem;

    div {
      width: 40%;
      height: 2px;
      border-bottom: 1px #cdcdcd dotted;
    }

    span {
      width: 20%;
      text-align: center;
      font-size: 1rem;
      font-style: italic;
      font-weight: 400;
      color: var(--color-text-primary);
    }
  }

  .n-input {
    width: 86%;
    height: 3.6rem;
    line-height: 3.6rem;
    box-sizing: border-box;
    text-align: center;
    margin-top: 1.2rem;
    font-size: 1rem;
    color: var(--color-text-primary);
    border-radius: 0.8rem;
    // border: 1px #cdcdcd solid; // 需要在 App.vue 中设置
  }

  .code-row {
    width: 86%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-top: -0.7rem;
  }

  .footer {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-self: center;
    width: 100%;
    padding: 0.8rem 0;
    left: 0;
    bottom: 0;
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;
    background-color: #f2f2f2;

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-self: center;
      font-size: 0.9rem;
      color: #6d6d6d;

      .link {
        font-weight: 500;
        color: #4d4d4d;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
</style>
